import { type EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { createClient } from 'graphql-ws';

import { environment } from '../../../environments/environment';
import { AuthStore } from '../auth/auth.store';

/**
 * Apollo Angular com split HTTP/WebSocket.
 *
 * - HTTP: queries + mutations via `HttpLink` (passa pelo `HttpClient` →
 *   authInterceptor injeta Bearer + 401-retry-com-refresh).
 * - WebSocket: subscriptions via `graphql-ws` em `wss://…/graphql`.
 *   Token JWT do localStorage no `connectionParams`. Reconnect com
 *   backoff exponencial (built-in da lib). `lazy: true` só abre a
 *   conexão quando há subscription ativa.
 *
 * `split()` rotea cada operação pro link certo baseado no kind.
 */
export function provideApollo(): EnvironmentProviders {
  return makeEnvironmentProviders([
    Apollo,
    {
      provide: APOLLO_OPTIONS,
      useFactory: (httpLink: HttpLink, authStore: AuthStore) => {
        const http = httpLink.create({
          uri: environment.graphqlUrl,
          withCredentials: true,
        });

        const ws = new GraphQLWsLink(
          createClient({
            url: environment.graphqlWsUrl,
            lazy: true,
            retryAttempts: 10,
            // JWT vem da memória (signal). Lib chama esta function a cada
            // (re)conexão, então pegará o token mais recente após login.
            connectionParams: () => {
              const token = authStore.sessionToken();
              return token ? { Authorization: `Bearer ${token}` } : {};
            },
          }),
        );

        const link: ApolloLink = split(
          ({ query }) => {
            const def = getMainDefinition(query);
            return (
              def.kind === 'OperationDefinition' &&
              def.operation === 'subscription'
            );
          },
          ws,
          http,
        );

        return {
          link,
          cache: new InMemoryCache(),
          defaultOptions: {
            watchQuery: { errorPolicy: 'all' },
            query: { errorPolicy: 'all' },
            mutate: { errorPolicy: 'all' },
          },
        };
      },
      deps: [HttpLink, AuthStore],
    },
  ]);
}
